import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { ZoomIn } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';

const FILTER_STYLES = {
  All: 'from-amber-400 to-orange-500',
  Events: 'from-rose-400 to-pink-500',
  Programmes: 'from-emerald-400 to-teal-500',
  Community: 'from-violet-400 to-fuchsia-500',
  Volunteers: 'from-sky-400 to-blue-500',
};

const Gallery = () => {
  const [filter, setFilter] = useState('All');
  const filters = Object.keys(FILTER_STYLES);
  const { data: galleryImages, loading } = useContent('gallery', []);

  const images = galleryImages || [];
  const filteredImages = filter === 'All' ? images : images.filter((img) => img.category === filter);

  if (!loading && images.length === 0) return null;

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-white to-amber-50/40" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeading
          eyebrow="Gallery"
          title="Moments that"
          accent="Matter"
          subtitle="A visual journey through EWRF events, programmes, and community initiatives."
        />

        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          {filters.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
                  active
                    ? `bg-gradient-to-r ${FILTER_STYLES[f]} text-white shadow-lg scale-105`
                    : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600'
                }`}
              >
                {f}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {filteredImages.map((image) => (
            <div
              key={image.id}
              className="group relative overflow-hidden rounded-2xl aspect-square cursor-pointer shadow-lg hover:shadow-2xl transition-shadow"
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-end p-5">
                <Badge className="mb-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 text-[10px] font-bold uppercase tracking-widest">
                  {image.category}
                </Badge>
                <p className="text-white text-sm md:text-base font-bold text-center drop-shadow-lg">{image.title}</p>
                <div className="mt-3 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/40">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
